import React, {useState} from "react";
import {useTranslation} from "react-i18next";

import "./style.less";

import {usePlatform} from "../../../../../../../../context/PlatformContext";
import {platformServices} from "../../../../../../../../services";
import Loading from "../../../../../../../../components/Loading";

const FUNCTIONALITY = "how_did_you_hear_about_us";
const LANGS = ["pt", "en", "es", "it"];

const emptyOption = () => ({pt: "", en: "", es: "", it: ""});

const isComplete = (option) => LANGS.every(l => (option?.[l] || "").trim().length > 0);

const HowDidYouHearAboutUs = () => {

    const {t} = useTranslation();
    const {platform, refreshPlatform} = usePlatform();

    const savedOptions = platform?.functionalities_extras?.[FUNCTIONALITY]?.options || [];

    // local working copy: existing saved options + any drafts being edited.
    // Each entry tracks whether it has been persisted already (savedPt) so we can
    // warn when the canonical PT identifier is being edited on a saved row.
    const [draft, setDraft] = useState(null); // null | option object for the new row
    const [edits, setEdits] = useState({}); // index -> partial option overrides
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const getOptionAt = (index) => ({...savedOptions[index], ...(edits[index] || {})});

    const setLangAt = (index, lang, value) => {
        setEdits(prev => ({
            ...prev,
            [index]: {...(prev[index] || {}), [lang]: value}
        }));
        if (error) setError(null);
    };

    const setDraftLang = (lang, value) => {
        setDraft(prev => ({...(prev || emptyOption()), [lang]: value}));
        if (error) setError(null);
    };

    const persist = async (nextOptions) => {
        setSaving(true);
        setError(null);
        try {
            await platformServices.setFunctionalityExtra(FUNCTIONALITY, {options: nextOptions});
            await refreshPlatform();
            return true;
        } catch (e) {
            setError(e?.error?.message || e?.message || "save_failed");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const commitEdit = async (index) => {
        const merged = getOptionAt(index);
        if (!isComplete(merged)) {
            setError("incomplete");
            return;
        }
        const next = savedOptions.map((o, i) => i === index ? merged : o);
        // basic duplicate check on PT (the canonical id)
        const dupe = next.findIndex((o, i) => i !== index && o.pt.trim() === merged.pt.trim());
        if (dupe !== -1) {
            setError("duplicate");
            return;
        }
        const ok = await persist(next);
        if (ok) {
            setEdits(prev => {
                const {[index]: _, ...rest} = prev;
                return rest;
            });
        }
    };

    const cancelEdit = (index) => {
        setEdits(prev => {
            const {[index]: _, ...rest} = prev;
            return rest;
        });
        if (error) setError(null);
    };

    const handleRemove = async (index) => {
        const next = savedOptions.filter((_, i) => i !== index);
        await persist(next);
    };

    const handleAddDraft = async () => {
        if (!draft || !isComplete(draft)) {
            setError("incomplete");
            return;
        }
        const trimmed = LANGS.reduce((acc, l) => ({...acc, [l]: draft[l].trim()}), {});
        if (savedOptions.some(o => o.pt.trim() === trimmed.pt)) {
            setError("duplicate");
            return;
        }
        const ok = await persist([...savedOptions, trimmed]);
        if (ok) setDraft(null);
    };

    const cancelDraft = () => {
        setDraft(null);
        if (error) setError(null);
    };

    const renderTranslationInputs = (option, onChange, disabled) => (
        <div className="HowDidYouHearAboutUs__inputs">
            {LANGS.map(lang => (
                <div className="HowDidYouHearAboutUs__field" key={lang}>
                    <label>{t(`lang.${lang}`)}</label>
                    <input
                        type="text"
                        className="input_field basic_container"
                        value={option?.[lang] || ""}
                        disabled={disabled}
                        placeholder={t(`lang.${lang}`)}
                        onChange={(e) => onChange(lang, e.target.value)}
                    />
                </div>
            ))}
        </div>
    );

    return (
        <section className="HowDidYouHearAboutUs basic_container">
            <header>
                <h3>{t("config.platform.how_did_you_hear_about_us.title")}</h3>
                <p>{t("config.platform.how_did_you_hear_about_us.description")}</p>
            </header>

            <div className="HowDidYouHearAboutUs__list">
                {savedOptions.length === 0 && !draft && (
                    <p className="HowDidYouHearAboutUs__empty">
                        {t("config.platform.how_did_you_hear_about_us.empty")}
                    </p>
                )}
                {savedOptions.map((saved, index) => {
                    const current = getOptionAt(index);
                    const dirty = !!edits[index];
                    const ptChanged = dirty && (edits[index]?.pt !== undefined) && edits[index].pt !== saved.pt;
                    const complete = isComplete(current);
                    return (
                        <div className="HowDidYouHearAboutUs__card basic_container" key={index}>
                            {renderTranslationInputs(
                                current,
                                (lang, value) => setLangAt(index, lang, value),
                                saving
                            )}
                            {ptChanged && (
                                <p className="HowDidYouHearAboutUs__warning">
                                    {t("config.platform.how_did_you_hear_about_us.pt_edit_warning")}
                                </p>
                            )}
                            <div className="HowDidYouHearAboutUs__card-actions">
                                <button
                                    type="button"
                                    className="btn btn-tertiary"
                                    disabled={saving}
                                    onClick={() => handleRemove(index)}>
                                    {t("buttons.remove")}
                                </button>
                                {dirty && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-tertiary"
                                            disabled={saving}
                                            onClick={() => cancelEdit(index)}>
                                            {t("buttons.cancel")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={saving || !complete}
                                            onClick={() => commitEdit(index)}>
                                            {t("buttons.save")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

                {draft && (
                    <div className="HowDidYouHearAboutUs__card basic_container HowDidYouHearAboutUs__card--new">
                        {renderTranslationInputs(draft, setDraftLang, saving)}
                        <div className="HowDidYouHearAboutUs__card-actions">
                            <button type="button" className="btn btn-tertiary" onClick={cancelDraft} disabled={saving}>
                                {t("buttons.cancel")}
                            </button>
                            <button type="button" className="btn btn-primary" onClick={handleAddDraft}
                                    disabled={saving || !isComplete(draft)}>
                                {t("buttons.add")}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {!draft && (
                <button type="button" className="btn btn-secondary HowDidYouHearAboutUs__add-btn"
                        onClick={() => setDraft(emptyOption())} disabled={saving}>
                    {`+ ${t("config.platform.how_did_you_hear_about_us.add_option")}`}
                </button>
            )}

            {error && <p className="error_message">
                {t(`config.platform.how_did_you_hear_about_us.errors.${error}`, t("config.platform.how_did_you_hear_about_us.errors.save_failed"))}
            </p>}

            {saving && <Loading cover={0.3}/>}
        </section>
    );
};

export default HowDidYouHearAboutUs;
